// ── 번호 생성 전략 공용 모듈 (React 의존 없음) ─────────────────────────────
// Random/Lucky/Smart 생성 로직을 UI 컴포넌트에서 분리한 순수 모듈.
// 모든 전략은 구조상 항상 유효한 조합(본 번호 6개 + 보너스 1개, 1~45 중복 없음)을
// 반환한다. 데이터(빈도/핫·콜드/최근 회차)가 부족하면 안전한 랜덤으로 대체한다.

import {
  generateSafeRandomCombination,
  type LottoCombination,
} from '@/lib/lottoCombination';

export const ALL_NUMBERS: number[] = Array.from({ length: 45 }, (_, i) => i + 1);

// ── 기초 유틸 ──

export function fisherYatesShuffle<T>(arr: readonly T[]): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export function pickRandom<T>(arr: readonly T[], count: number): T[] {
  return fisherYatesShuffle(arr).slice(0, count);
}

export function getOddEvenCount(nums: readonly number[]): { odd: number; even: number } {
  const odd = nums.filter((n) => n % 2 === 1).length;
  return { odd, even: 6 - odd };
}

export function getRangeCount(nums: readonly number[]): number[] {
  const ranges = [0, 0, 0, 0, 0]; // 1-10, 11-20, 21-30, 31-40, 41-45
  nums.forEach((n) => {
    if (n <= 10) ranges[0]++;
    else if (n <= 20) ranges[1]++;
    else if (n <= 30) ranges[2]++;
    else if (n <= 40) ranges[3]++;
    else ranges[4]++;
  });
  return ranges;
}

export function getTotalSum(nums: readonly number[]): number {
  return nums.reduce((a, b) => a + b, 0);
}

export function hasConsecutive(nums: readonly number[]): number {
  const sorted = [...nums].sort((a, b) => a - b);
  let count = 0;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] === 1) count++;
  }
  return count;
}

export function getEndDigitCounts(nums: readonly number[]): Map<number, number> {
  const map = new Map<number, number>();
  nums.forEach((n) => {
    const d = n % 10;
    map.set(d, (map.get(d) || 0) + 1);
  });
  return map;
}

export function maxEndDigitCount(nums: readonly number[]): number {
  const map = getEndDigitCounts(nums);
  let max = 0;
  map.forEach((c) => {
    if (c > max) max = c;
  });
  return max;
}

// ── 기본 랜덤 ──

/** 옵션(제외수/고정수) 없이 완전 랜덤으로 조합을 생성한다. */
export function generateBasicRandom(): LottoCombination {
  return generateSafeRandomCombination();
}

// ── 고정/제외 번호 랜덤 ──

/**
 * 제외수/고정수를 반영한 랜덤 생성.
 * - 고정수(fixed)는 결과에 반드시 포함, 제외수(excluded)는 결과에서 배제
 * - 고정수와 제외수가 충돌하면 고정수를 우선한다
 * - 고정수가 6개 이상이면 앞 6개만 사용
 * - 제외수로 인해 풀이 부족하면 일부 제외를 무시하는 fallback
 * - 항상 유효한 조합(6개 + 보너스)을 반환한다
 */
export function generateFixedExcludeRandom(
  fixedNumbers: readonly number[],
  excludedNumbers: readonly number[],
): LottoCombination {
  const fixed = [...new Set(fixedNumbers)].filter(
    (n) => Number.isInteger(n) && n >= 1 && n <= 45,
  );
  const excluded = [...new Set(excludedNumbers)].filter(
    (n) => Number.isInteger(n) && n >= 1 && n <= 45 && !fixed.includes(n),
  );

  const available = ALL_NUMBERS.filter((n) => !excluded.includes(n));

  const selected: number[] = fixed.slice(0, 6);
  const needCount = 6 - selected.length;

  if (needCount <= 0) {
    const result = selected.slice(0, 6).sort((a, b) => a - b);
    const bonusPool = available.filter((n) => !result.includes(n));
    const bonus =
      bonusPool.length > 0
        ? bonusPool[Math.floor(Math.random() * bonusPool.length)]
        : (ALL_NUMBERS.find((n) => !result.includes(n)) ?? 1);
    return { numbers: result, bonus };
  }

  const pool = available.filter((n) => !selected.includes(n));

  if (pool.length < needCount) {
    // 제외수 일부를 무시하는 fallback: 고정수를 제외한 전체 번호에서 선택
    const fallbackPool = ALL_NUMBERS.filter((n) => !selected.includes(n));
    selected.push(...fisherYatesShuffle(fallbackPool).slice(0, needCount));
  } else {
    selected.push(...fisherYatesShuffle(pool).slice(0, needCount));
  }

  const result = selected.slice().sort((a, b) => a - b);

  const bonusPool = available.filter((n) => !result.includes(n));
  const bonus =
    bonusPool.length > 0
      ? bonusPool[Math.floor(Math.random() * bonusPool.length)]
      : (ALL_NUMBERS.find((n) => !result.includes(n)) ?? 1);

  return { numbers: result, bonus };
}

// ── 행운 생성기 (결정론적: 동일 입력 → 동일 출력) ──

export function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * 이름 + 생년월일 기반 행운 번호(DJB2 해시 + LCG).
 * 같은 입력은 항상 같은 번호를 반환한다(결정론적).
 */
export function generateLucky(name: string, birthDate: string): LottoCombination {
  const nameHash = hashString(name.trim());
  const dateStr = birthDate.replace(/-/g, '');
  const dateHash = hashString(dateStr);
  const combinedSeed = nameHash ^ dateHash;

  const nums: number[] = [];
  let seed = combinedSeed;
  while (nums.length < 6) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const rand = (seed % 45) + 1;
    if (!nums.includes(rand)) nums.push(rand);
  }
  nums.sort((a, b) => a - b);

  let bonusNum: number;
  do {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    bonusNum = (seed % 45) + 1;
  } while (nums.includes(bonusNum));

  return { numbers: nums, bonus: bonusNum };
}

// ── 스마트 추천 전략 ──

/** 출현 빈도가 높은 번호일수록 더 높은 확률로 선택. 빈도 데이터 부족 시 안전 랜덤. */
export function generateWeighted(freqMap: Map<number, number>): LottoCombination {
  if (!freqMap || freqMap.size < 7) return generateSafeRandomCombination();

  const pool: number[] = [];
  freqMap.forEach((count, num) => {
    if (!Number.isInteger(num) || num < 1 || num > 45) return;
    const weight = count + 1; // +1 so cold numbers still have a chance
    for (let i = 0; i < weight; i++) pool.push(num);
  });
  if (pool.length === 0) return generateSafeRandomCombination();

  const selected: number[] = [];
  const used = new Set<number>();
  while (selected.length < 6) {
    const idx = Math.floor(Math.random() * pool.length);
    const num = pool[idx];
    if (!used.has(num)) {
      used.add(num);
      selected.push(num);
    }
  }
  selected.sort((a, b) => a - b);

  const remaining = ALL_NUMBERS.filter((n) => !used.has(n));
  const bonus = remaining[Math.floor(Math.random() * remaining.length)];
  return { numbers: selected, bonus };
}

/** 홀짝 3:3 + 번호대 분산 + 총합 100~175 + 연속 1쌍 이하 + 끝수 제한. */
export function generateBalanced(): LottoCombination {
  for (let attempt = 0; attempt < 500; attempt++) {
    const oddPool = ALL_NUMBERS.filter((n) => n % 2 === 1);
    const evenPool = ALL_NUMBERS.filter((n) => n % 2 === 0);

    const selectedOdds = pickRandom(oddPool, 3);
    const selectedEvens = pickRandom(evenPool, 3);
    const nums = [...selectedOdds, ...selectedEvens].sort((a, b) => a - b);

    const ranges = getRangeCount(nums);
    const emptyRanges = ranges.filter((r) => r === 0).length;
    if (emptyRanges > 1) continue;

    const sum = getTotalSum(nums);
    if (sum < 100 || sum > 175) continue;

    if (hasConsecutive(nums) > 1) continue;
    if (maxEndDigitCount(nums) > 2) continue;

    const bonusPool = ALL_NUMBERS.filter((n) => !nums.includes(n));
    const bonus = bonusPool[Math.floor(Math.random() * bonusPool.length)];
    return { numbers: nums, bonus };
  }

  const fallback = pickRandom(ALL_NUMBERS, 7);
  return { numbers: fallback.slice(0, 6).sort((a, b) => a - b), bonus: fallback[6] };
}

/** 핫 넘버 3개 + 콜드 넘버 3개 조합. 핫/콜드 목록 부족 시 안전 랜덤. */
export function generateHotCold(hotNums: readonly number[], coldNums: readonly number[]): LottoCombination {
  const validHot = [...new Set(hotNums)].filter(
    (n) => Number.isInteger(n) && n >= 1 && n <= 45,
  );
  const validCold = [...new Set(coldNums)].filter(
    (n) => Number.isInteger(n) && n >= 1 && n <= 45 && !validHot.includes(n),
  );
  if (validHot.length < 3 || validCold.length < 3) return generateSafeRandomCombination();

  const selected = pickRandom(validHot, 3).concat(pickRandom(validCold, 3));
  selected.sort((a, b) => a - b);

  const bonusPool = ALL_NUMBERS.filter((n) => !selected.includes(n));
  const bonus = bonusPool[Math.floor(Math.random() * bonusPool.length)];
  return { numbers: selected, bonus };
}

/** 최근 10회차 미출현 번호에 더 높은 가중치. 데이터 부족 시 안전 랜덤. */
export function generateRecentCold(
  freqMap: Map<number, number>,
  recentDraws: ReadonlyArray<{ numbers: number[] }>,
): LottoCombination {
  if (!freqMap || freqMap.size < 7) return generateSafeRandomCombination();

  const recentNums = new Set<number>();
  const lookback = Math.min(10, recentDraws.length);
  for (let i = 0; i < lookback; i++) {
    const nums = recentDraws[i]?.numbers ?? [];
    nums.forEach((n) => recentNums.add(n));
  }

  const notSeenRecently = ALL_NUMBERS.filter((n) => !recentNums.has(n));

  const pool: number[] = [];
  freqMap.forEach((count, num) => {
    if (!Number.isInteger(num) || num < 1 || num > 45) return;
    const isCold = !recentNums.has(num);
    const weight = (count + 1) * (isCold ? 3 : 1);
    for (let i = 0; i < weight; i++) pool.push(num);
  });
  if (pool.length === 0) return generateSafeRandomCombination();

  const selected: number[] = [];
  const used = new Set<number>();

  // 최소 2개는 미출현 번호로 보장
  const mustPick = Math.min(2, notSeenRecently.length);
  const coldPicks = pickRandom(notSeenRecently, mustPick);
  coldPicks.forEach((n) => {
    used.add(n);
    selected.push(n);
  });

  while (selected.length < 6) {
    const idx = Math.floor(Math.random() * pool.length);
    const num = pool[idx];
    if (!used.has(num)) {
      used.add(num);
      selected.push(num);
    }
  }
  selected.sort((a, b) => a - b);

  const bonusPool = ALL_NUMBERS.filter((n) => !used.has(n));
  const bonus = bonusPool[Math.floor(Math.random() * bonusPool.length)];
  return { numbers: selected, bonus };
}

/** 같은 끝자리 번호가 2개를 넘지 않도록 제한. */
export function generateEndDigit(): LottoCombination {
  for (let attempt = 0; attempt < 300; attempt++) {
    const nums = pickRandom(ALL_NUMBERS, 6).sort((a, b) => a - b);
    if (maxEndDigitCount(nums) <= 2) {
      const bonusPool = ALL_NUMBERS.filter((n) => !nums.includes(n));
      const bonus = bonusPool[Math.floor(Math.random() * bonusPool.length)];
      return { numbers: nums, bonus };
    }
  }

  const fallback = pickRandom(ALL_NUMBERS, 7);
  return { numbers: fallback.slice(0, 6).sort((a, b) => a - b), bonus: fallback[6] };
}